import { BodyM, Caption, Description, colorVariants } from '@casavo/base-ui'
import cn from 'classnames'
import React, { memo } from 'react'

import PropertyIcon from '@/components/PropertyIcon'
import { useDevice } from '@/context/Device'
import { Badge } from '@/design-lib/components/Badge'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { ellipsis, marginRight, paddingBottom } from '@/shared-styles/styles.css'
import { CardDataListing } from '@/types/property-card'

import { FavoriteButtonGtmTouchpoint, FavouriteButton } from '../FavouriteButton'
import Icon from '../Icon'

import { cardBody, cardMainImage, cardTitle, iconsInnerWrapper, iconsOuterWrapper } from './index.css'

export type Props = {
  badge?: {
    left?: React.ReactNode
  }
  cardData: CardDataListing
  details: React.ReactNode[]
  favoriteButtonGtmTouchpoint: FavoriteButtonGtmTouchpoint
  favouriteClick?: (listingId: string) => void
  favouriteEnabled?: boolean
  hasOutdoorSpace?: boolean
  headline?: React.ReactElement
  image: {
    alt: string
    src: string
  }
  info: {
    heading: string
    icon?: 'rooms' | 'bedrooms' | 'bathroom'
    text: string | number
  }[]
  isHighEfficiency?: boolean
  isOwnedByCasavo: boolean
  labels?: {
    highEfficienyLabel?: string
    outdoorSpaceLabel?: string
  }
  price: string
  title: string
}

const Card: React.FC<Props> = memo((props: Props) => {
  const device = useDevice()

  const imageHeight = device === 'desktop' ? 200 : 160

  return (
    <div className={sprinkles({ display: 'flex', flex: 1, flexDirection: 'column', maxWidth: '100%', width: '100%' })}>
      <div className={cardMainImage}>
        {props.badge ? props.badge.left : null}
        <img
          alt={props.image.alt}
          className={cn('card-suggested', cardMainImage)}
          data-testid="card-main-img"
          height={imageHeight}
          src={props.image.src + '?auto=compress,format&h=' + imageHeight}
          srcSet={
            props.image.src +
            '?auto=compress,format&h=' +
            imageHeight +
            '&dpr=1 1x,' +
            props.image.src +
            '?auto=compress,format&h=' +
            imageHeight +
            '&dpr=2 2x,' +
            props.image.src +
            '?auto=compress,format&h=' +
            imageHeight +
            '&dpr=3 3x'
          }
          style={{ height: imageHeight }}
          width="100%"
        />
      </div>
      <div className={cardBody}>
        {props.headline && (
          <Caption className={cn(paddingBottom.xs, ellipsis)} color="greyscale500">
            {props.headline}
          </Caption>
        )}

        <Description strong as="h2" className={cardTitle}>
          {props.title}
        </Description>

        <div className={sprinkles({ marginBottom: 'm' })}>
          {props.details.length > 0 && (
            <div className={sprinkles({ display: 'flex', gap: 's' })}>
              {props.details.map((detail, index) => (
                <div key={index}>{detail}</div>
              ))}
            </div>
          )}

          {props.info.length > 0 && (
            <div className={sprinkles({ display: 'flex', gap: 'm', marginTop: 'auto' })}>
              {props.info.map((info, index) => (
                <div key={index}>
                  <Caption color="greyscale500">{info.heading}</Caption>
                  <div className={sprinkles({ alignItems: 'center', display: 'flex', gap: 'xs' })}>
                    <Caption>{info.text}</Caption>
                    {info.icon && <PropertyIcon kind={info.icon} />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {props.favouriteEnabled ? (
          <div className={sprinkles({ marginTop: 'auto' })}>
            {(props?.hasOutdoorSpace || props?.isHighEfficiency) && (
              <div className={iconsOuterWrapper}>
                {/* icons */}
                <div className={iconsInnerWrapper}>
                  {props?.hasOutdoorSpace && (
                    <Badge className={sprinkles({ marginRight: 'xs' })} icon="Tree" styleVariant="greyscale" />
                  )}
                  {props?.isHighEfficiency && (
                    <Badge className={sprinkles({ marginRight: 'xs' })} icon="Sun" styleVariant="greyscale" />
                  )}
                </div>
              </div>
            )}
            <div>
              {/* price + favorite? */}
              <div
                className={sprinkles({
                  alignItems: 'center',
                  display: 'flex',
                  flexShrink: 0,
                  justifyContent: 'space-between',
                  marginLeft: 'auto',
                })}
              >
                <div className={sprinkles({ alignItems: 'center', display: 'flex' })}>
                  {props.isOwnedByCasavo && (
                    <Icon.Check
                      aria-hidden="true"
                      className={cn(marginRight.s, colorVariants.orange600)}
                      data-testid="casavo-ribbon"
                    />
                  )}

                  <BodyM>{props.price}</BodyM>
                </div>
                <FavouriteButton
                  gtmTouchpoint={props.favoriteButtonGtmTouchpoint}
                  id={props.cardData.id}
                  {...(props.favouriteClick
                    ? {
                        favouriteClick: (e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          props.favouriteClick(props.cardData.id)
                        },
                      }
                    : {})}
                />
              </div>
            </div>
          </div>
        ) : (
          <div
            className={sprinkles({
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              marginTop: 'auto',
              width: '100%',
            })}
            style={{ height: 23 }}
          >
            {/* icons */}
            <div
              className={sprinkles({
                alignItems: 'flex-start',
                display: 'flex',
                flex: 1,
                marginRight: 's',
                overflow: 'hidden',
              })}
            >
              {props?.hasOutdoorSpace && (
                <Badge className={sprinkles({ marginRight: 'xs' })} icon="Tree" styleVariant="greyscale" />
              )}
              {props?.isHighEfficiency && (
                <Badge className={sprinkles({ marginRight: 'xs' })} icon="Sun" styleVariant="greyscale" />
              )}
            </div>
            {/* price + favorite? */}
            <div
              className={sprinkles({
                alignItems: 'center',
                display: 'flex',
                flexShrink: 0,
                marginLeft: 'auto',
              })}
            >
              {props.isOwnedByCasavo && (
                <Icon.Check
                  aria-hidden="true"
                  className={cn(marginRight.s, colorVariants.orange600)}
                  data-testid="casavo-ribbon"
                />
              )}

              <BodyM>{props.price}</BodyM>
            </div>
            {props.favouriteEnabled && (
              <div>
                <FavouriteButton
                  gtmTouchpoint={props.favoriteButtonGtmTouchpoint}
                  id={props.cardData.id}
                  {...(props.favouriteClick ? { onClick: () => props.favouriteClick(props.cardData.id) } : {})}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
})

Card.displayName = 'Card'

export default Card
